import React, { useEffect, useState } from 'react';
import { LaunchBanner } from '../components/LaunchBanner';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronRight, CheckCircle2, Circle, Calendar, Lock, Share2 } from 'lucide-react';
import { programs as fallbackPrograms } from '../data/calmFeaturesData';
import { useContent } from '../context/ContentContext';
import BreathingCircle from '../components/BreathingCircle';
import ShareSheet from '../components/ShareSheet';
import { useAuth } from '../context/AuthContext';
import { logMindfulSession } from '../utils/mindful';
import { toast } from 'sonner';
import { FlagshipProgramsSection } from '../components/programs/FlagshipProgramsSection';
import FlagshipProgramPage from './FlagshipProgramPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const buildProgramShareContent = (p) => {
  if (!p) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return {
    kind: 'program',
    id: p.id,
    title: p.name,
    text: `Join me on "${p.name}" — ${p.tagline} · ${p.sessions.length} sessions on Reflections by Rowdy`,
    url: `${origin}/programs/${p.id}`,
    recommendable: true,
  };
};

export const ProgramsIndexPage = () => {
  const navigate = useNavigate();
  const { programs } = useContent();
  const list = programs && programs.length ? programs : fallbackPrograms;
  const [shareProgram, setShareProgram] = useState(null);

  return (
    <div className="animate-fade-in pb-8" data-testid="programs-index-page">
      <div className="pt-4"><LaunchBanner placement="programs" /></div>
      <div className="px-5 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-[#D6AD60] transition-colors duration-300 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl btn-obsidian flex items-center justify-center shadow-md">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#f9f4ea]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Programs
            </h1>
            <p className="text-sm text-[#e3d2a8]/80">Guided journeys with Rowdy — one short session a day</p>
          </div>
        </div>
      </div>

      <FlagshipProgramsSection />

      <div className="px-5 pb-3">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#D6AD60] mb-1">Short Series</p>
        <h2 className="text-lg font-bold text-[#f9f4ea]" style={{ fontFamily: "'Playfair Display', serif" }}>Quick multi-day resets</h2>
      </div>

      <div className="px-5 flex flex-col gap-4" data-testid="legacy-programs-list">
        {list.map((p) => (
          <div key={p.id} className="relative">
            <Link
              to={`/programs/${p.id}`}
              className="relative block rounded-3xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 group"
              data-testid={`program-card-${p.id}`}
            >
              <img src={p.image} alt={p.name} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: p.accent }}>
                  {p.sessions.length} sessions
                </p>
                <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {p.name}
                </h3>
                <p className="text-xs text-white/80">{p.tagline}</p>
              </div>
            </Link>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShareProgram(p);
              }}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-stone-700 hover:text-[#D6AD60] flex items-center justify-center shadow-md backdrop-blur-md transition-colors"
              aria-label={`Share ${p.name}`}
              data-testid={`program-share-${p.id}`}
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <ShareSheet
        open={!!shareProgram}
        onClose={() => setShareProgram(null)}
        content={buildProgramShareContent(shareProgram)}
      />
    </div>
  );
};


export const ProgramDetailPage = () => {
  const { programId } = useParams();
  const { getProgramById } = useContent();
  if (!getProgramById(programId)) return <FlagshipProgramPage slug={programId} />;
  return <LegacyProgramDetailPage programId={programId} />;
};

const LegacyProgramDetailPage = ({ programId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getProgramById, getBreathingExerciseById } = useContent();
  const program = getProgramById(programId);
  const [completed, setCompleted] = useState([]); // array of session ids
  const [active, setActive] = useState(null); // session being run
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!user) return;
    const token = localStorage.getItem('rbr_token');
    fetch(`${API_URL}/api/programs/${programId}/progress`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { completed: [] }))
      .then((d) => setCompleted(d.completed || []))
      .catch(() => setCompleted([]));
  }, [programId, user]);

  if (!program) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-5">
        <p className="text-stone-500 mb-4">Program not found.</p>
        <button
          onClick={() => navigate('/programs')}
          className="px-5 py-2.5 btn-gold rounded-full text-sm font-semibold"
        >
          Back to programs
        </button>
      </div>
    );
  }

  const completeSession = async (session) => {
    if (!user) {
      // Sessions are open to everyone — only saved progress needs a profile.
      toast('Sign in to track your progress', { description: 'Every session stays open to you either way.' });
      return;
    }
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/programs/${programId}/sessions/${session.id}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCompleted(data.progress.completed);
        await logMindfulSession({
          kind: session.kind === 'breath' ? 'breath' : 'meditation',
          durationSec: (session.minutes || 3) * 60,
          ref: session.ref,
        });
        toast.success(`Day ${session.day} complete`);
      }
    } catch (e) {
      toast.error('Could not save progress');
    }
  };

  const startSession = (session) => {
    if (session.kind === 'breath') {
      const ex = getBreathingExerciseById(session.ref);
      if (ex) {
        setActive({ session, exercise: ex });
        return;
      }
    }
    // Post-based session — open the post; PostDetailPage auto-completes the
    // program session if the user lingers (read params on the URL).
    navigate(
      `/post/${session.ref}?program=${programId}&session=${session.id}`
    );
  };

  const completedSet = new Set(completed);
  const completedCount = program.sessions.filter((s) => completedSet.has(s.id)).length;
  const totalMinutes = program.sessions.reduce((sum, s) => sum + (s.minutes || 0), 0);
  const progressPct = Math.round((completedCount / program.sessions.length) * 100);

  return (
    <div className="animate-fade-in pb-8" data-testid={`program-detail-${program.id}`}>
      {/* Hero */}
      <div className="relative">
        <img src={program.image} alt={program.name} className="w-full h-60 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={() => setShareOpen(true)}
          className="absolute top-4 right-4 w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
          aria-label="Share program"
          data-testid="program-detail-share-btn"
        >
          <Share2 className="w-4 h-4 text-white" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: program.accent }}>
            {program.sessions.length} sessions · ~{totalMinutes} min total
          </p>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            {program.name}
          </h1>
        </div>
      </div>

      <div className="px-5 pt-5">
        <p className="text-sm text-stone-600 leading-relaxed mb-5">{program.description}</p>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#D6AD60] uppercase tracking-wider">Your progress</p>
            <span className="text-xs text-stone-500" data-testid="program-progress-count">
              {completedCount} of {program.sessions.length}
            </span>
          </div>
          <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D6AD60] to-[#D6AD60] rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Sessions list */}
        <div className="flex flex-col gap-2.5">
          {program.sessions.map((s, idx) => {
            const isDone = completedSet.has(s.id);
            // Lock subsequent sessions until previous is done (gentle progression)
            const isLocked =
              user && idx > 0 && !completedSet.has(program.sessions[idx - 1].id) && !isDone;
            return (
              <button
                key={s.id}
                onClick={() => !isLocked && startSession(s)}
                disabled={isLocked}
                className={`flex items-center gap-3 p-3.5 rounded-2xl shadow-sm text-left transition-all border ${
                  isDone
                    ? 'bg-emerald-50/70 border-emerald-200'
                    : isLocked
                    ? 'bg-stone-100 border-stone-200 opacity-60 cursor-not-allowed'
                    : 'bg-[#FFFCF3] border-stone-200 hover:border-[#D6AD60]/30 hover:shadow-md active:scale-[0.99]'
                }`}
                data-testid={`program-session-${s.id}`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isDone ? 'bg-emerald-100' : 'bg-[#E8DDCB]'
                  }`}
                >
                  {isLocked ? (
                    <Lock className="w-4 h-4 text-stone-400" />
                  ) : isDone ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <Circle className="w-5 h-5 text-[#D6AD60]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Day {s.day} · ~{s.minutes} min
                  </p>
                  <p className="text-sm font-semibold text-stone-800 truncate">{s.title}</p>
                </div>
                {!isLocked && <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active breathing session overlay */}
      {active && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-stone-900/85 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-md mx-4">
            <button
              onClick={() => {
                completeSession(active.session);
                setActive(null);
              }}
              className="absolute -top-2 right-0 px-4 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium flex items-center justify-center transition-colors"
              data-testid="program-session-finish"
            >
              Finish session
            </button>
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 ml-1">
              {program.name} · Day {active.session.day}
            </p>
            <BreathingCircle
              pattern={{
                inhale: active.exercise.inhale,
                hold: active.exercise.hold || 0,
                exhale: active.exercise.exhale,
              }}
              title={active.exercise.name}
            />
          </div>
        </div>
      )}

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        content={buildProgramShareContent(program)}
      />
    </div>
  );
};
